import { runAudioStore } from './audioIdb';
import { notifyAudioLibraryChanged } from './audioLibraryEvents';
import { createTrackId } from './audioPlaylistStorage';
import { sanitizeTileColor, tileLabelForSave } from './audioTileAppearance';
import {
  cloudDeleteGenreStation,
  cloudListGenreStations,
  cloudSaveGenreStation,
  hasCloudAudioLibrary,
} from './audioPlayerLibraryApi';
import {
  deleteLocalGenreStation,
  mirrorGenreStationToLocal,
  mirrorGenreStationsToLocal,
} from './audioPlayerLocalMirror';

const STORE = 'genreStations';

async function readGenreStationsFromIdb(genreId) {
  return runAudioStore(STORE, 'readonly', (store) => {
    if (genreId) {
      const index = store.index('genreId');
      const req = index.getAll(String(genreId));
      return new Promise((resolve, reject) => {
        req.onsuccess = () => {
          const rows = (req.result || []).slice();
          rows.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          resolve(rows);
        };
        req.onerror = () => reject(req.error);
      });
    }
    const req = store.getAll();
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function listGenreStationsLocalOnly(genreId) {
  return readGenreStationsFromIdb(genreId);
}

export async function listGenreStations(genreId) {
  if (await hasCloudAudioLibrary()) {
    const rows = await cloudListGenreStations(genreId);
    if (!genreId) {
      await mirrorGenreStationsToLocal(rows);
    }
    if (genreId) {
      const filtered = rows.filter((r) => String(r.genreId) === String(genreId));
      filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      return filtered;
    }
    return rows;
  }

  return readGenreStationsFromIdb(genreId);
}

export async function saveGenreStation({ id, genreId, title, subtitle, url, tileLabel, tileColor }) {
  const trimmedUrl = String(url || '').trim();
  if (!trimmedUrl || !genreId) {
    throw new Error('Genre and stream URL are required.');
  }
  const stationTitle = String(title || 'Custom station').trim() || 'Custom station';
  const payload = {
    id: id || undefined,
    genreId: String(genreId),
    title: stationTitle,
    subtitle: String(subtitle || 'Web stream').trim() || 'Web stream',
    url: trimmedUrl,
    tileLabel: tileLabelForSave(tileLabel, stationTitle),
    tileColor: sanitizeTileColor(tileColor),
  };

  if (await hasCloudAudioLibrary()) {
    const saved = await cloudSaveGenreStation(payload);
    await mirrorGenreStationToLocal(saved);
    notifyAudioLibraryChanged({
      store: 'genreStations',
      id: saved.id,
      genreId: saved.genreId,
      cloud: true,
    });
    return saved;
  }

  const entry = {
    ...payload,
    id: payload.id || createTrackId(),
    savedAt: Date.now(),
  };
  await runAudioStore(STORE, 'readwrite', (store) => store.put(entry));
  notifyAudioLibraryChanged({ store: 'genreStations', id: entry.id, genreId: entry.genreId });
  return entry;
}

export async function deleteGenreStation(id) {
  if (!id) return;
  if (await hasCloudAudioLibrary()) {
    await cloudDeleteGenreStation(id);
    await deleteLocalGenreStation(id);
    notifyAudioLibraryChanged({ store: 'genreStations', id, deleted: true, cloud: true });
    return;
  }
  await runAudioStore(STORE, 'readwrite', (store) => store.delete(String(id)));
  notifyAudioLibraryChanged({ store: 'genreStations', id, deleted: true });
}

/** @returns {Map<string, object[]>} */
export function groupGenreStationsByGenreId(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const gid = row.genreId;
    if (!gid) return;
    if (!map.has(gid)) map.set(gid, []);
    map.get(gid).push(row);
  });
  map.forEach((list, gid) => {
    list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    map.set(gid, list);
  });
  return map;
}
